import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { FormSelect } from '@components/common/FormSelect/FormSelect';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { ApplicationFormPageEnum } from '../types';
import { fullNameQuestions } from './questions';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { YesNoEnum } from '../HouseholdOutgoings/types';
import { IFullNameForm } from './types';
import { ISelectOption } from '@components/common/Select/types';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';
import { getCurrentRoute } from '@utils/location';

export const FullName = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const formApplicantNames = searchParams.get('applicantNames')?.split(',') || [];
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IFullNameForm>({
    defaultValues: {
      title: null,
      firstName: '',
      lastName: '',
      isNameChanged: '',
    },
  });

  const formFields = form.watch();

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IFullNameForm) => {
    const isNameChanged = formData.isNameChanged === YesNoEnum.YES;
    const nextPage = isNameChanged ? ApplicationFormPageEnum.PREVIOUS_NAME : ApplicationFormPageEnum.PERSONAL_DETAILS;

    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: fullNameQuestions[key].labelKey ? intl.formatMessage({ id: fullNameQuestions[key].labelKey }) : '',
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('applicantNames', [...formApplicantNames, formData.firstName].join(','));
    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  const {
    title: titleQuestion,
    firstName: firstNameQuestion,
    lastName: lastNameQuestion,
    isNameChanged: isNameChangedQuestion,
  } = fullNameQuestions;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={firstNameQuestion.labelKey} tagName={'h4'} />
          <FormattedMessage id={firstNameQuestion.explanationKey} tagName={'p'} />

          <div className={'grid grid-cols-3 gap-4'}>
            <FormSelect
              name={'title'}
              options={titleQuestion.options}
              className={'col-span-1'}
              selectProps={{
                isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                  option.value === selectedOptionValue,
                value: formFields.title,
              }}
              selectInputProps={{ placeholder: intl.formatMessage({ id: 'placeholders.title' }) }}
              rules={{ required: requiredFieldErrorMessage }}
            />
            <FormTextField
              name={'firstName'}
              rules={{
                required: requiredFieldErrorMessage,
                pattern: {
                  value: /^[a-zA-Z\s'\-.]*$/,
                  message: intl.formatMessage({ id: 'common.error.invalidCharacters' }),
                },
              }}
              textFieldProps={{ className: 'col-span-2' }}
            />
          </div>
        </section>

        <section>
          <FormattedMessage id={lastNameQuestion.labelKey} tagName={'h4'} />
          <FormattedMessage id={lastNameQuestion.explanationKey} tagName={'p'} />

          <FormTextField
            name={'lastName'}
            rules={{
              required: requiredFieldErrorMessage,
              pattern: {
                value: /^[a-zA-Z\s'\-.]*$/,
                message: intl.formatMessage({ id: 'common.error.invalidCharacters' }),
              },
            }}
          />
        </section>

        <section>
          <FormattedMessage id={isNameChangedQuestion.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'isNameChanged'}
            radioOptions={isNameChangedQuestion.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </section>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
